import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { getDayJsFromNgbDateStruct } from '@mwe/utils';
import dayjs from 'dayjs';
import { maxBirthDate, maxUmmeldedatum, minBirthDate, minUmmeldedatum } from './datepicker';

export class CustomValidators {
  static readonly DEFAULT_MIN_LENGTH = 2;
  static readonly FIRMENBUCH_NR_MAX_LENGTH = 12;
  static readonly STREET_MAX_LENGTH = 100;
  static readonly METER_NUMBER_MIN_LENGTH = 4;
  static readonly CUSTOMER_NUMBER_REGEX = /^[A-Za-z0-9]+$/;
  static readonly VK_NR_REGEX = /^\d+$/;
  static readonly ANY_LETTER_REGEX = /^[\p{Script=Latin}\p{M}\s'`´-]+$/u;
  static readonly PHONE_NUMBER_REGEX = /^(?:\+|00)\d(?:[\d\s]*\d)?$/;
  static readonly WHITESPACE_REGEX = /\s/;
  static readonly WHITESPACE_GLOBAL_REGEX = /\s/g;
  static readonly UID_REGEX = /^[A-Za-z]{2}[A-Za-z0-9]+$/;
  static readonly FIRMENBUCH_NR_REGEX = /^[A-Za-z0-9]+$/;
  static readonly HOUSE_NUMBER_STAIRCASE_REGEX = /^(?=.*[A-Za-z0-9])[A-Za-z0-9 ./-]+$/;
  static readonly PLZ_REGEX = /^\d{4}$/;
  static readonly CITY_REGEX = /^[\p{Script=Latin}\p{M}\s.'`´-]+$/u;
  static readonly STREET_REGEX = /^[\p{Script=Latin}\p{M}0-9 .'`´-]+$/u;
  static readonly METER_POINT_NUMBER_REGEX = /^[A-Z0-9]{12,}$/;
  static readonly METER_NUMBER_REGEX = /^\w+$/;
  static readonly METER_COUNT_REGEX = /^\s*\d+(,\d*)?\s*$/;
  static readonly DOOR_NUMBER_REGEX = /^[a-zA-Z0-9\s/\-.,]+$/;

  static firstNameValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.nameValidator(control, 'firstName');
  }

  static lastNameValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.nameValidator(control, 'lastName');
  }

  static companyNameValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();

    if (!value) {
      return { translationKey: `inputErrors.blank.companyName` };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: `inputErrors.tooShort.companyName` };
    }
    return null;
  }

  static nameValidator(control: AbstractControl, fieldName: string): ValidationErrors | null {
    const value = control.value?.trim();

    if (!value) {
      return { translationKey: `inputErrors.blank.${fieldName}` };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: `inputErrors.tooShort.${fieldName}` };
    }
    if (!CustomValidators.ANY_LETTER_REGEX.test(value)) {
      return { translationKey: `inputErrors.invalid.${fieldName}` };
    }
    return null;
  }

  static emailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.email' };
    }
    if (CustomValidators.WHITESPACE_REGEX.test(value) || Validators.email({ value } as AbstractControl)) {
      return { translationKey: 'inputErrors.invalid.email' };
    }
    return null;
  }

  static dateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return { translationKey: 'inputErrors.blank.datePicker' };
    }
    const { day, month, year } = value;
    const invalidDate = { translationKey: 'inputErrors.invalid.datePicker' };

    if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
      return invalidDate;
    }

    const calendarDate = new Date(0);
    calendarDate.setUTCFullYear(year, month - 1, day);
    calendarDate.setUTCHours(0, 0, 0, 0);

    if (calendarDate.getUTCFullYear() !== year || calendarDate.getUTCMonth() !== month - 1 || calendarDate.getUTCDate() !== day) {
      return invalidDate;
    }

    return null;
  }

  static birthdateValidator(control: AbstractControl): ValidationErrors | null {
    const dateValidation = CustomValidators.dateValidator(control);

    if (dateValidation) {
      return dateValidation;
    }

    const birthdate = getDayJsFromNgbDateStruct(control.value);

    if (!birthdate.isValid()) {
      return { translationKey: 'inputErrors.invalid.datePicker' };
    }

    if (birthdate.isBefore(getDayJsFromNgbDateStruct(minBirthDate))) {
      return { translationKey: 'inputErrors.birthdate.tooOld' };
    }

    if (birthdate.isAfter(dayjs().startOf('day'))) {
      return { translationKey: 'inputErrors.birthdate.inFuture' };
    }

    if (birthdate.isAfter(getDayJsFromNgbDateStruct(maxBirthDate))) {
      return { translationKey: 'inputErrors.birthdate.tooYoung' };
    }
    return null;
  }

  static ummeldedatumValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null;
    }

    const dateValidation = CustomValidators.dateValidator(control);

    if (dateValidation) {
      return dateValidation;
    }

    const ummeldedatum = getDayJsFromNgbDateStruct(value);

    if (
      ummeldedatum.isBefore(getDayJsFromNgbDateStruct(minUmmeldedatum)) ||
      ummeldedatum.isAfter(getDayJsFromNgbDateStruct(maxUmmeldedatum))
    ) {
      return { translationKey: 'inputErrors.invalid.ummeldeDatum' };
    }

    return null;
  }

  static optionalBirthdateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    return CustomValidators.birthdateValidator(control);
  }

  static optionalPhoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    if (value.length < 8 || value.length > 20 || !CustomValidators.PHONE_NUMBER_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.phoneNumber' };
    }
    return null;
  }

  static uidValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.uidNr' };
    }
    if (value.length < 8) {
      return { translationKey: 'inputErrors.tooShort.uidNr' };
    }
    if (value.length > 14) {
      return { translationKey: 'inputErrors.tooLong.uidNr' };
    }
    if (!CustomValidators.UID_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.uidNr' };
    }
    return null;
  }

  static optionalUidValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    return CustomValidators.uidValidator(control);
  }

  static optionalEmailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    return CustomValidators.emailValidator(control);
  }

  static optionalFirmenbuchNrValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return null;
    }
    if (!CustomValidators.FIRMENBUCH_NR_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.firmenbuchNr' };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.firmenbuchNr' };
    }
    if (value.length > CustomValidators.FIRMENBUCH_NR_MAX_LENGTH) {
      return { translationKey: 'inputErrors.tooLong.firmenbuchNr' };
    }
    return null;
  }

  static plzValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.plz' };
    }
    if (value.length < 4) {
      return { translationKey: 'inputErrors.tooShort.plz' };
    }
    if (value.length > 4) {
      return { translationKey: 'inputErrors.tooLong.plz' };
    }
    if (!CustomValidators.PLZ_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.plz' };
    }
    return null;
  }

  static cityValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.city' };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.city' };
    }
    if (!CustomValidators.CITY_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.city' };
    }
    return null;
  }

  static streetValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.street' };
    }
    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.street' };
    }
    if (value.length > CustomValidators.STREET_MAX_LENGTH) {
      return { translationKey: 'inputErrors.tooLong.street' };
    }
    if (!CustomValidators.STREET_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.street' };
    }
    return null;
  }

  static houseNumberStaircaseValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.addressNumberValidator(control, 'houseNumberStaircase');
  }

  static houseNumberValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.addressNumberValidator(control, 'houseNumber');
  }

  static staircaseValidator(control: AbstractControl): ValidationErrors | null {
    return CustomValidators.addressNumberValidator(control, 'staircase');
  }

  private static addressNumberValidator(
    control: AbstractControl,
    field: 'houseNumber' | 'staircase' | 'houseNumberStaircase',
  ): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: `inputErrors.blank.${field}` };
    }
    if (!CustomValidators.HOUSE_NUMBER_STAIRCASE_REGEX.test(value)) {
      return { translationKey: `inputErrors.invalid.${field}` };
    }
    return null;
  }

  static doorNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();

    if (!value) {
      return null;
    }

    if (!CustomValidators.DOOR_NUMBER_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.door' };
    }

    return null;
  }

  static meterPointNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return { translationKey: 'inputErrors.blank.meterPointNumber' };
    }
    const valueWithoutWhitespace = CustomValidators.getValueWithoutWhitespace(value);
    if (!CustomValidators.METER_POINT_NUMBER_REGEX.test(valueWithoutWhitespace)) {
      return { translationKey: 'inputErrors.invalid.meterPointNumber' };
    }
    return null;
  }

  static meterNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.meterNumber' };
    }
    if (!CustomValidators.METER_NUMBER_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.meterNumber' };
    }
    if (value.length < CustomValidators.METER_NUMBER_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.meterNumber' };
    }
    return null;
  }

  static meterCountValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    if (!value) {
      return { translationKey: 'inputErrors.blank.meterCount' };
    }
    if (!CustomValidators.METER_COUNT_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.meterCount' };
    }
    if (value.length > 20) {
      return { translationKey: 'inputErrors.tooLong.meterCount' };
    }
    return null;
  }

  static vkNrValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value ?? '').trim();

    if (!value) {
      return { translationKey: 'registration.verification.input.accountNumber.blank' };
    }

    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'registration.verification.input.accountNumber.tooShort' };
    }

    if (!CustomValidators.VK_NR_REGEX.test(value)) {
      return { translationKey: 'registration.verification.input.accountNumber.invalid' };
    }

    return null;
  }

  static customerNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value ?? '').trim();

    if (!value) {
      return { translationKey: 'inputErrors.blank.customerNumber' };
    }

    if (CustomValidators.WHITESPACE_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.customerNumber' };
    }

    if (value.length < CustomValidators.DEFAULT_MIN_LENGTH) {
      return { translationKey: 'inputErrors.tooShort.customerNumber' };
    }

    if (!CustomValidators.CUSTOMER_NUMBER_REGEX.test(value)) {
      return { translationKey: 'inputErrors.invalid.customerNumber' };
    }

    return null;
  }

  static optionalCustomerNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value ?? '').trim();

    return value ? CustomValidators.customerNumberValidator(control) : null;
  }

  private static getValueWithoutWhitespace(value: string): string {
    return value?.replace(CustomValidators.WHITESPACE_GLOBAL_REGEX, '');
  }
}
